import { useBanner } from 'apps/website/src/utils/useBanner';
import { SiteLocale } from 'apps/website/types/dato';
import { TagBlockProps } from 'libs/growth-platform-brand-system-v2/src/components/Tag';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import React, { useRef, useState } from 'react';
import bannerThemes, { TBannerThemes } from './bannerThemes';
import { BannerContent, BannerControls } from './components';
import { useBannerAnimation, useOverflowDetection, useScrollDetection } from './hooks';

interface ITopBanner {
  items: {
    tag?: TagBlockProps[];
    text: string;
    id: string;
  }[];
  color: TBannerThemes;
  interval?: number;
  locale: SiteLocale;
}

const animationSettings = {
  exit: { opacity: 0, y: -30 },
  exitTransition: { duration: 300 / 1000 },
  initial: { opacity: 0, y: 20 },
  animateTransition: { duration: 300 / 1000, delay: 300 / 1000 },
};

export const TopBanner = ({
  items,
  locale,
  color = 'light',
  interval = 5000
}: ITopBanner) => {
  const { bannerVisible, bannerClose } = useBanner();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  const { collapseTimeRef } = useBannerAnimation({
    items,
    interval,
    isExpanded,
    isHovered,
    onIndexChange: setCurrentIndex,
  });

  const { hasOverflow, expandedHeight, collapsedHeight } = useOverflowDetection({
    contentRef,
    currentText: items[currentIndex]?.text,
  });

  const { isScrolledPast } = useScrollDetection({
    bannerRef,
  });

  const handleExpandToggle = (expanded: boolean) => {
    setIsExpanded(expanded);
    if (!expanded) {
      collapseTimeRef.current = Date.now();
    }
  };

  const handleBannerClick = (e: React.MouseEvent) => {
    if (
      e.target instanceof HTMLElement &&
      (e.target.closest('a') || e.target.closest('button'))
    ) {
      return;
    }

    if (window.innerWidth <= 768) {
      handleExpandToggle(!isExpanded);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
          setIsExpanded(false);
        }
        break;
      case 'ArrowRight':
        if (currentIndex < items.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setIsExpanded(false);
        }
        break;
    }
  };

  if (!bannerVisible || !items || items.length === 0) return null;

  return (
    <div
      ref={bannerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleBannerClick}
      onKeyDown={handleKeyDown}
      role="alert"
      aria-live="polite"
      tabIndex={0}
      className={classNames(
        'Banner relative mb-8 z-20 transition-opacity duration-300 md:cursor-default cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2',
        bannerThemes[color],
        isScrolledPast ? 'opacity-0 pointer-events-none' : 'opacity-100'
      )}
    >
      <BannerContent
        currentItem={items[currentIndex]}
        isExpanded={isExpanded}
        expandedHeight={expandedHeight}
        collapsedHeight={collapsedHeight}
        contentRef={contentRef}
        animationSettings={animationSettings}
        color={color}
      />

      <BannerControls
        isExpanded={isExpanded}
        hasOverflow={hasOverflow}
        color={color}
        locale={locale}
        onExpandToggle={handleExpandToggle}
        onClose={bannerClose}
      />
    </div>
  );
};
